/**
 * perfLog — lightweight step-timing so we can SEE how long each action takes (tap → saved → modal)
 * instead of guessing. Jose 2026-10-23: "you can't compound what you can't see." Keeps the last ~120
 * entries in localStorage + logs each to the console. View anytime with `window.slikPerf()` (console.table)
 * or `window.slikPerf.slow()` for the slowest. This is the instrument for the Brain Audit + optimization.
 */
type PerfEntry = { label: string; ms: number; at: number };
const KEY = 'slik-perf-log';
const MAX = 120;

function record(label: string, ms: number): void {
  const rounded = Math.round(ms);
  try {
    const arr: PerfEntry[] = JSON.parse(localStorage.getItem(KEY) || '[]');
    arr.push({ label, ms: rounded, at: Date.now() });
    while (arr.length > MAX) arr.shift();
    localStorage.setItem(KEY, JSON.stringify(arr));
  } catch { /* storage full / private mode — console still logs */ }
  // Amber if it's getting slow so it jumps out in the console during a shift.
  const bg = rounded > 1500 ? '#f59e0b' : rounded > 600 ? '#eab308' : '#0ea5e9';
  // eslint-disable-next-line no-console
  console.log(`%c[perf]%c ${label}: ${rounded}ms`, `background:${bg};color:#fff;padding:1px 6px;border-radius:3px;font-weight:bold`, 'color:inherit');
}

/** Start a timer; call the returned fn to stamp the elapsed time. `const stop = perfStart('book'); … stop();` */
export function perfStart(label: string): () => void {
  const t0 = performance.now();
  let stopped = false;
  return () => { if (!stopped) { stopped = true; record(label, performance.now() - t0); } };
}

/** Time an async action end-to-end. */
export async function perfMeasure<T>(label: string, fn: () => Promise<T>): Promise<T> {
  const t0 = performance.now();
  try { return await fn(); } finally { record(label, performance.now() - t0); }
}

export function getPerfLog(): PerfEntry[] {
  try { return JSON.parse(localStorage.getItem(KEY) || '[]'); } catch { return []; }
}
export function clearPerfLog(): void { try { localStorage.removeItem(KEY); } catch { /* */ } }

// Console helpers — zero-friction to read during the optimization weeks.
if (typeof window !== 'undefined') {
  const api: any = () => { const l = getPerfLog(); try { console.table(l); } catch { /* */ } return l; };
  api.slow = (n = 10) => { const l = getPerfLog().slice().sort((a, b) => b.ms - a.ms).slice(0, n); try { console.table(l); } catch { /* */ } return l; };
  api.clear = () => clearPerfLog();
  (window as any).slikPerf = api;
}
